import React from "react";
import { FiLogOut, FiX } from 'react-icons/fi';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, useSidebar } from "@/components/ui/sidebar";
import BullseyeIcon from "@/components/common/icons/BullseyeIcon";
import ChartIcon from "@/components/common/icons/ChartIcon";
import MoneyIcon from "@/components/common/icons/MoneyIcon";

export function AppSidebar({
  tabs = [],
  activeTab,
  onTabChange,
  quickStats = [],
  dataSnapshot = [],
  quickActions = [],
  userEmail,
  onLogout,
}) {
  const { isMobile, setOpenMobile } = useSidebar();

  return (
    <Sidebar variant="inset" className="border-transparent bg-sidebar/90 text-sidebar-foreground">
      <SidebarHeader className="px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground">
              <BullseyeIcon aria-hidden="true" className="size-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">Tombola Admin</h1>
              <p className="text-sm text-sidebar-foreground/70">Slovenský zväz posunkujúcich</p>
            </div>
          </div>
          {isMobile && (
            <button
              onClick={() => setOpenMobile(false)}
              className="flex size-8 items-center justify-center rounded-lg text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
              aria-label="Zatvoriť sidebar"
            >
              <FiX className="size-5" />
            </button>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="gap-6 px-4 pb-6">
        <SidebarGroup>
          <SidebarGroupLabel>Navigácia</SidebarGroupLabel>
          <SidebarMenu>
            {tabs.map((tab) => (
              <SidebarMenuItem key={tab.id}>
                <SidebarMenuButton
                  isActive={activeTab === tab.id}
                  onClick={() => onTabChange?.(tab.id)}
                  className="justify-start gap-3"
                >
                  {tab.icon ? <tab.icon className="size-5" /> : null}
                  <span className="font-medium">{tab.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        {quickStats.length > 0 && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>Rýchly prehľad</SidebarGroupLabel>
              <SidebarGroupContent>
                <div className="space-y-3 text-sm text-sidebar-foreground/80">
                  {quickStats.map((stat) => (
                    <div key={stat.label} className="flex items-center justify-between">
                      <span>{stat.label}</span>
                      <span className={`font-medium text-sidebar-foreground ${stat.valueClass ?? ""}`}>
                        {stat.value}
                      </span>
                    </div>
                  ))}
                </div>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {dataSnapshot.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Prehľad dát</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="space-y-3 text-sm text-sidebar-foreground/80">
                {dataSnapshot.map((stat) => (
                  <div key={stat.label} className="flex items-center justify-between">
                    <span>{stat.label}:</span>
                    <span className="font-medium text-sidebar-foreground">{stat.value}</span>
                  </div>
                ))}
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {quickActions.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Rýchle akcie</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="space-y-2 text-sm">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    type="button"
                    onClick={action.onClick}
                    className="flex w-full items-center gap-2 rounded-lg border border-sidebar-border bg-sidebar-accent/30 px-3 py-2 text-left text-sidebar-foreground transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    {action.icon === "money" && <MoneyIcon className="text-success-300" aria-hidden="true" />}
                    {action.icon === "bullseye" && <BullseyeIcon aria-hidden="true" />}
                    {action.icon === "report" && <ChartIcon className="text-primary-300" aria-hidden="true" />}
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="px-4 pb-6 space-y-3">
        <div className="text-xs text-sidebar-foreground/60">
          <p>
            Prihlásený používateľ:{' '}
            <span className="font-medium text-sidebar-foreground">{userEmail ?? '—'}</span>
          </p>
        </div>
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-2 rounded-lg border border-sidebar-border bg-sidebar-accent/30 px-3 py-2 text-sm text-sidebar-foreground transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:border-error-500/50 hover:text-error-400"
          >
            <FiLogOut className="size-4" />
            <span>Odhlásiť sa</span>
          </button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
